import { DEFAULT_WIF_REQUEST_PARAMETER } from "@p0security/shared/types/agentic/credential";
import {
  type McpServerSpec,
  propagationHint,
} from "@p0security/shared/types/agentic/mcp";
import { mapValues, omit } from "lodash-es";

import { GcloudIconUrl } from "../icon.ts";
import {
  gcpBigQueryMcpToolIamPermissions,
  gcpComputeMcpToolIamPermissions,
  gcpIamMcpToolIamPermissions,
  gcpStorageMcpToolIamPermissions,
} from "./tools/iam-mapping.ts";
import { gcpIamMcpTools } from "./tools/tools.ts";

/** An access type offered by a Google Cloud MCP server, bundling the CLI
 * arguments that select it with the fields listable under it. Split into the
 * `access` / `list` halves of the server spec by {@link multiAccessTypeGcpMcp}.
 */
type GcpAccessTypeSpec = {
  /** Prompt describing how to form argv for this access type */
  prompt: string;
  /** Arguments selecting this access type, following the `gcloud` resource type */
  args: string[];
  /** Fields listable under this access type, beyond the common `project` */
  fields: McpServerSpec["list"]["fields"];
};

const googleCloud = "Google Cloud";

const projectField = {
  description: `${googleCloud} project ID`,
  required: [],
};

const gcpPropagationHint = propagationHint({
  resource: "GCP",
  normalWait: "15 to 45 seconds",
  maxWait: "2 minutes",
  pollInterval: "10 seconds",
});

/** Image reference for a P0-run agentic backend container. Deployments pin
 * the image through the env var (set from iac/kubernetes terraform); the
 * untagged fallback applies only outside one (local dev, tests). Guarded
 * because this module is also bundled into the frontend, where `process`
 * can be undefined. */
export const imageFromEnv = (args: { env: string; fallback: string }): string =>
  (typeof process === "undefined" ? undefined : process.env[args.env]) ||
  args.fallback;

/** Hosting, credential, and location attributes shared by every Google Cloud MCP
 * server, independent of which access types it serves.
 */
const gcpMcpBase = (args: {
  url: string;
  label: string;
}): Omit<McpServerSpec & { type: "external" }, "access" | "list"> => ({
  type: "external",
  label: args.label,
  iconUrl: GcloudIconUrl,
  credential: {
    type: "wif",
    csp: "gcp",
    parameter: DEFAULT_WIF_REQUEST_PARAMETER,
  },
  authorization: "upstream",
  tools: { type: "web", url: args.url },
  url: args.url,
});

/** A Google Cloud MCP server serving exactly one access type, pinned in
 * `prefixArgs`. The agent needs no `access_type` argument.
 */
const sharedGcpMcp = (args: {
  url: string;
  label: string;
  prompt: McpServerSpec["access"]["prompt"];
  accessType: string;
  extraFields: McpServerSpec["list"]["fields"];
}): McpServerSpec & { type: "external" } => ({
  ...gcpMcpBase(args),
  access: {
    prompt: args.prompt,
    prefixArgs: ["gcloud", args.accessType],
    propagationHint: gcpPropagationHint,
  },
  list: {
    fields: {
      project: projectField,
      ...args.extraFields,
    },
    prefixArgs: ["gcloud", args.accessType],
  },
});

// If we add more GCP types or need it dynamic per mcp server we'll want to refactor
const gcloudAccessTypePromptDescription = `This access can be requested various ways. Choose one and pass it as access_type:

  - "resource": scoped to a single resource. For PLP, prefer using the list tool and requesting only resource level access.
  - "role": project-wide. Use only when the calling tools must span multiple resources, or when you can't determine which resource to name.`;

/** A Google Cloud MCP server serving several access types. Only the `gcloud`
 * resource type is pinned in `prefixArgs`; the access-type positional (and any
 * flags that go with it) come from the selected variant, so the agent must pass
 * `access_type` to both the `access` and `list` tools.
 */
const multiAccessTypeGcpMcp = (args: {
  url: string;
  label: string;
  prompt: McpServerSpec["access"]["prompt"];
  accessTypes: Record<string, GcpAccessTypeSpec>;
}): McpServerSpec & { type: "external" } => ({
  ...gcpMcpBase(args),
  access: {
    prompt: args.prompt,
    prefixArgs: ["gcloud"],
    propagationHint: gcpPropagationHint,
    accessTypes: mapValues(args.accessTypes, ({ prompt, args: typeArgs }) => ({
      prompt,
      args: typeArgs,
    })),
  },
  list: {
    prefixArgs: ["gcloud"],
    // The union of every access type's fields; the gateway narrows to the
    // selected access type's `accessTypes[...].fields` when listing.
    fields: Object.values(args.accessTypes).reduce(
      (all, { fields }) => ({ ...all, ...fields }),
      { project: projectField }
    ),
    accessTypes: mapValues(args.accessTypes, ({ args: typeArgs, fields }) => ({
      args: typeArgs,
      fields: { project: projectField, ...fields },
    })),
  },
});

const gcpLocatorPrompt = `Pass the item's full JSON resource descriptor (key field) verbatim for the <locator>.`;

export const gcpComputeMcp: McpServerSpec & { type: "external" } = {
  ...multiAccessTypeGcpMcp({
    url: "https://compute.googleapis.com/mcp",
    label: `${googleCloud} Compute`,
    prompt: `Request Google Cloud Compute access.

${gcloudAccessTypePromptDescription}
`,
    accessTypes: {
      resource: {
        prompt: `For access_type "resource", call with argv:

  [<locator>, <accesses>, "--project", <project>, "--reason", <reason>]

where:

  - locator: The instance to which access is requested; format is JSON blob with format described below.
    Example: '{"fullName":"//compute.googleapis.com/projects/{project}/zones/{zone}/instances/{name}","name":"{name}","service":"compute","type":"instance"}'.
  - accesses: Google Cloud Compute role (e.g. 'roles/compute.instanceAdmin.v1' for full instance access)
`,
        args: ["resource"],
        fields: {
          locator: {
            description: "Google Cloud Compute locator (instance)",
            required: ["project"],
            prompt: gcpLocatorPrompt,
          },
          accesses: {
            description: "Google Cloud Compute role",
            required: ["project"],
            prompt: "Use 'compute.' to list all available roles",
          },
        },
      },
      role: {
        prompt: `For access_type "role", call with argv:

  [<role>, "--project", <project>, "--reason", <reason>]

where:

  - role: Google Cloud Compute role granted across the whole project
    (e.g. 'compute.viewer')
`,
        args: ["role"],
        fields: {
          name: {
            description: "Google Cloud Compute role, granted project-wide",
            required: ["project"],
            prompt: "Use 'compute.' to list all available roles",
          },
        },
      },
    },
  }),
  toolPermissions: gcpComputeMcpToolIamPermissions,
};

// Google doesn't host an MCP server for IAM, so this is a P0-built image
// (docker/gcp-iam in oauthed-mcp-tools) implementing tools
// directly against iam.googleapis.com, rather than bridging to one of
// Google's own hosted MCP endpoints.
export const gcpIamMcp: McpServerSpec & { type: "container" } = {
  type: "container",
  label: `${googleCloud} IAM`,
  iconUrl: GcloudIconUrl,
  credential: {
    type: "wif",
    csp: "gcp",
    parameter: DEFAULT_WIF_REQUEST_PARAMETER,
  },
  authorization: "upstream",
  transport: "stdio",
  tools: {
    type: "manual",
    // `iamPermissions` is an authoring-time tag (see mcp-tools.ts); strip it
    // here so it never reaches the gateway wire as part of a tool.
    tools: gcpIamMcpTools.map((tool) => omit(tool, ["iamPermissions"])),
  },
  toolPermissions: gcpIamMcpToolIamPermissions,
  container: {
    command: [],
    args: [],
    envs: {
      GCP_BEARER_TOKEN: "{{credential.token}}",
    },
    image: {
      image: "p0security/gcp-iam:latest",
    },
  },
  access: {
    prompt: `Request Google Cloud IAM access by calling with argv:

  [<role>, "--project", <project>, "--reason", <reason>]

where arguments are:

  - project: Google Cloud project ID
  - reason: Business justification for requested access
  - role: Google Cloud IAM role (e.g. 'iam.serviceAccountViewer')
`,
    prefixArgs: ["gcloud", "role"],
    propagationHint: gcpPropagationHint,
  },
  list: {
    fields: {
      project: projectField,
      name: {
        description: "Google Cloud IAM role",
        required: ["project"],
        prompt: `Use 'iam.' to list all available IAM roles`,
      },
    },
    prefixArgs: ["gcloud", "role"],
  },
  location: {
    key: "project",
    description: `${googleCloud} project`,
  },
};

export const gcpMonitoringMcp = sharedGcpMcp({
  url: "https://monitoring.googleapis.com/mcp",
  label: `${googleCloud} Monitoring`,
  prompt: `Request Google Cloud Monitoring access by calling with argv:

  [<role>, "--project", <project>, "--reason", <reason>]

where arguments are:

  - project: Google Cloud project ID
  - reason: Business justification for requested access
  - role: Google Cloud Monitoring role (e.g. 'monitoring.viewer')
`,
  accessType: "role",
  extraFields: {
    name: {
      description: "Google Cloud Monitoring role",
      required: ["project"],
      prompt: `Use 'monitoring.' to list all available monitoring roles`,
    },
  },
});

export const gcpLoggingMcp = sharedGcpMcp({
  url: "https://logging.googleapis.com/mcp",
  label: `${googleCloud} Logging`,
  prompt: `Request Google Cloud Logging access by calling with argv:

  [<role>, "--project", <project>, "--reason", <reason>]

where arguments are:

  - project: Google Cloud project ID
  - reason: Business justification for requested access
  - role: Google Cloud Logging role (e.g. 'logging.viewer')
`,
  accessType: "role",
  extraFields: {
    name: {
      description: "Google Cloud Logging role",
      required: ["project"],
      prompt: `Use 'logging.' to list all available logging roles`,
    },
  },
});

export const gcpBigQueryMcp: McpServerSpec & { type: "external" } = {
  ...multiAccessTypeGcpMcp({
    url: "https://bigquery.googleapis.com/mcp",
    label: `${googleCloud} BigQuery`,
    prompt: `Request Google BigQuery access.

${gcloudAccessTypePromptDescription}
`,
    accessTypes: {
      resource: {
        prompt: `For access_type "resource", call with argv:

  [<locator>, <accesses>, "--project", <project>, "--reason", <reason>]

where:

  - locator: The dataset or table to which access is requested; format is JSON blob with format described below.
    Example: '{"fullName":"//bigquery.googleapis.com/projects/p0-gcp-project/datasets/my-dataset","name":"my-dataset","service":"bigquery","type":"dataset"}'.
  - accesses: Google Cloud BigQuery role (e.g. 'bigquery.dataViewer')
`,
        args: ["resource"],
        fields: {
          locator: {
            description: "Google BigQuery locator (dataset or table)",
            required: ["project"],
            prompt: gcpLocatorPrompt,
          },
          accesses: {
            description: "Google BigQuery role",
            required: ["project"],
            prompt: "Use 'bigquery.' to list all available roles",
          },
        },
      },
      role: {
        prompt: `For access_type "role", call with argv:

  [<role>, "--project", <project>, "--reason", <reason>]

where:

  - role: Google Cloud BigQuery role granted across the whole project
    (e.g. 'bigquery.dataEditor')
`,
        args: ["role"],
        fields: {
          // `role` is a generated parameter with no `ls` leaf; the listable role field
          // is `name` (see roleParameters in ./accesses.ts).
          name: {
            description: "Google Cloud BigQuery role",
            required: ["project"],
            prompt: "Use 'bigquery.' to list all available BigQuery roles",
          },
        },
      },
    },
  }),
  toolPermissions: gcpBigQueryMcpToolIamPermissions,
};

export const gcpStorageMcp: McpServerSpec & { type: "external" } = {
  ...multiAccessTypeGcpMcp({
    url: "https://storage.googleapis.com/storage/mcp",
    label: `${googleCloud} Storage`,
    prompt: `Request Google Cloud Storage access.

${gcloudAccessTypePromptDescription}
`,
    accessTypes: {
      resource: {
        prompt: `For access_type "resource", call with argv:

  [<locator>, <accesses>, "--project", <project>, "--reason", <reason>]

where:

  - locator: The bucket to which access is requested; format is JSON blob with format described below.
    Example: '{"fullName":"//storage.googleapis.com/my-bucket","name":"my-bucket","service":"storage","type":"bucket"}'.
  - accesses: Google Cloud Storage role (e.g. 'storage.objectViewer' for read-only, 'storage.objectUser' for read/write)
`,
        args: ["resource"],
        fields: {
          locator: {
            description: "Google Cloud Storage locator (bucket)",
            required: ["project"],
            prompt: gcpLocatorPrompt,
          },
          accesses: {
            description: "Google Cloud Storage role",
            required: ["project"],
            prompt: "Use 'storage.' to list all available roles",
          },
        },
      },
      role: {
        prompt: `For access_type "role", call with argv:

  [<role>, "--project", <project>, "--reason", <reason>]

where:

  - role: Google Cloud Storage role granted across the whole project
    (e.g. 'storage.objectViewer')
`,
        args: ["role"],
        fields: {
          name: {
            description: "Google Cloud Storage role, granted project-wide",
            required: ["project"],
            prompt: "Use 'storage.' to list all available roles",
          },
        },
      },
    },
  }),
  toolPermissions: gcpStorageMcpToolIamPermissions,
};

// Firestore's IAM roles are named for Datastore (e.g. 'datastore.user'), so
// roles are listed under the 'datastore.' prefix rather than 'firestore.'.
export const gcpFirestoreMcp = sharedGcpMcp({
  url: "https://firestore.googleapis.com/mcp",
  label: `${googleCloud} Firestore`,
  prompt: `Request Google Cloud Firestore access by calling with argv:

  [<role>, "--project", <project>, "--reason", <reason>]

where arguments are:

  - project: Google Cloud project ID
  - reason: Business justification for requested access
  - role: Google Cloud Firestore role (e.g. 'datastore.viewer' for read-only, 'datastore.user' for read/write)
`,
  accessType: "role",
  extraFields: {
    name: {
      description: "Google Cloud Firestore role",
      required: ["project"],
      prompt: `Use 'datastore.' to list all available Firestore roles`,
    },
  },
});
